import { useCallback, useEffect, useRef, useState } from "react";
import { FlatList, Keyboard, Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text, TextInput } from "react-native-paper";

import { parseAddressValue, serializeAddressValue } from "./address-helpers";
import type { QuestionComponentProps } from "./types";

import { ensureMapsLoaded } from "@/lib/maps-loader";

// Mirrors apps/mobile/app/(auth)/register.tsx — same pattern that already
// works for the RN registration screen and TravelMap. On web the address
// dropdown uses the Google Maps JavaScript SDK (loaded once via a <script>
// tag in document.head, then we call AutocompleteService + PlacesService).
// On native we fall back to the REST endpoint, same as the registration
// screen does today. No backend involved.

const GOOGLE_MAPS_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";

interface Prediction {
    description: string;
    placeId: string;
}

interface PlaceDetails {
    address: string;
    lat: number;
    lng: number;
}

// ── Web: access the Places API after ensureMapsLoaded() has resolved ────

type PlacesAPI = {
    AutocompleteService: new () => unknown;
    PlacesService: new (el: HTMLElement) => unknown;
};

function getPlacesAPI(): PlacesAPI | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }
    const google = (window as unknown as { google?: { maps?: { places?: PlacesAPI } } }).google;
    return google?.maps?.places;
}

// ── Web prediction + details via the JS SDK ────────────

async function fetchPredictionsWeb(input: string): Promise<Prediction[]> {
    await ensureMapsLoaded();
    const places = getPlacesAPI();
    if (!places) {
        return [];
    }
    const svc = new places.AutocompleteService() as {
        getPlacePredictions: (
            req: { input: string; types: string[]; componentRestrictions: { country: string } },
            cb: (results: Array<{ description: string; place_id: string }> | null) => void
        ) => void;
    };
    return new Promise<Prediction[]>((resolve) => {
        svc.getPlacePredictions(
            { input, types: ["address"], componentRestrictions: { country: "us" } },
            (results) => {
                resolve(
                    (results ?? []).map((p) => ({
                        description: p.description,
                        placeId: p.place_id
                    }))
                );
            }
        );
    });
}

let webPlacesServiceEl: HTMLDivElement | null = null;
let webPlacesService: unknown | null = null;

async function fetchPlaceDetailsWeb(placeId: string): Promise<PlaceDetails | null> {
    await ensureMapsLoaded();
    const places = getPlacesAPI();
    if (!places) {
        return null;
    }
    if (!webPlacesService) {
        webPlacesServiceEl = document.createElement("div");
        webPlacesService = new places.PlacesService(webPlacesServiceEl);
    }
    const svc = webPlacesService as {
        getDetails: (
            req: { placeId: string; fields: string[] },
            cb: (result: unknown, status: string) => void
        ) => void;
    };
    return new Promise<PlaceDetails | null>((resolve) => {
        svc.getDetails(
            { placeId, fields: ["formatted_address", "geometry"] },
            (result: unknown, status: string) => {
                if (status !== "OK" || !result) {
                    resolve(null);
                    return;
                }
                const r = result as {
                    formatted_address?: string;
                    geometry?: { location?: { lat: () => number; lng: () => number } };
                };
                if (!r.geometry?.location) {
                    resolve(null);
                    return;
                }
                resolve({
                    address: r.formatted_address ?? "",
                    lat: r.geometry.location.lat(),
                    lng: r.geometry.location.lng()
                });
            }
        );
    });
}

// ── Native fallback: REST endpoints (same as register.tsx fallback) ────

async function fetchPredictionsNative(input: string): Promise<Prediction[]> {
    if (!GOOGLE_MAPS_API_KEY) {
        return [];
    }
    try {
        const url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(
            input
        )}&types=address&components=country:us&key=${GOOGLE_MAPS_API_KEY}`;
        const res = await fetch(url);
        const data = (await res.json()) as {
            predictions?: Array<{ description: string; place_id: string }>;
        };
        return (data.predictions ?? []).map((p) => ({
            description: p.description,
            placeId: p.place_id
        }));
    } catch {
        return [];
    }
}

async function fetchPlaceDetailsNative(placeId: string): Promise<PlaceDetails | null> {
    if (!GOOGLE_MAPS_API_KEY) {
        return null;
    }
    try {
        const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${encodeURIComponent(
            placeId
        )}&fields=formatted_address,geometry&key=${GOOGLE_MAPS_API_KEY}`;
        const res = await fetch(url);
        const data = (await res.json()) as {
            result?: {
                formatted_address?: string;
                geometry?: { location?: { lat: number; lng: number } };
            };
        };
        const result = data.result;
        if (!result?.geometry?.location) {
            return null;
        }
        return {
            address: result.formatted_address ?? "",
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng
        };
    } catch {
        return null;
    }
}

const fetchPredictions = (input: string): Promise<Prediction[]> =>
    Platform.OS === "web" ? fetchPredictionsWeb(input) : fetchPredictionsNative(input);
const fetchPlaceDetails = (placeId: string): Promise<PlaceDetails | null> =>
    Platform.OS === "web" ? fetchPlaceDetailsWeb(placeId) : fetchPlaceDetailsNative(placeId);

export function AddressQuestion({
    question,
    value,
    onChange,
    readOnly,
    onFocus
}: QuestionComponentProps) {
    const parsed = parseAddressValue(value);
    const [inputText, setInputText] = useState(parsed.address);
    const [predictions, setPredictions] = useState<Prediction[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Track the canonical formatted address from the last selection. Used
    // to decide whether a keystroke is "still editing the selection" (keep
    // coords) or "drifted off it" (wipe coords). Fixes the prior behavior
    // where every keystroke after a selection nulled lat/lng.
    const selectedAddressRef = useRef<string | null>(
        parsed.lat != null && parsed.lng != null ? parsed.address : null
    );

    // Sync inputText + selection snapshot when the parent value changes
    // (server hydration, draft restore, etc.).
    useEffect(() => {
        const next = parseAddressValue(value);
        setInputText(next.address);
        selectedAddressRef.current = next.lat != null && next.lng != null ? next.address : null;
    }, [value]);

    const handleTextChange = useCallback(
        (text: string) => {
            setInputText(text);

            // Only zero lat/lng when the typed text genuinely diverges from
            // the previously-selected address. While the user is still on
            // the canonical text (or backspacing through it), keep the
            // coords intact.
            const sel = selectedAddressRef.current;
            const matchesSelection = sel != null && (text === sel || sel.startsWith(text));
            if (matchesSelection) {
                const prev = parseAddressValue(value);
                onChange(serializeAddressValue({ address: text, lat: prev.lat, lng: prev.lng }));
            } else {
                selectedAddressRef.current = null;
                onChange(serializeAddressValue({ address: text, lat: null, lng: null }));
            }

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = setTimeout(async () => {
                if (text.length < 3) {
                    setPredictions([]);
                    setShowSuggestions(false);
                    return;
                }
                const results = await fetchPredictions(text);
                setPredictions(results);
                setShowSuggestions(results.length > 0);
            }, 300);
        },
        [onChange, value]
    );

    const handleSelect = useCallback(
        async (prediction: Prediction) => {
            setShowSuggestions(false);
            setPredictions([]);
            Keyboard.dismiss();

            const details = await fetchPlaceDetails(prediction.placeId);
            if (details) {
                setInputText(details.address);
                selectedAddressRef.current = details.address;
                onChange(serializeAddressValue(details));
            } else {
                setInputText(prediction.description);
                selectedAddressRef.current = null;
                onChange(
                    serializeAddressValue({
                        address: prediction.description,
                        lat: null,
                        lng: null
                    })
                );
            }
        },
        [onChange]
    );

    // No API key at all — render plain text input as a graceful degrade.
    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <TextInput
                mode="outlined"
                value={inputText}
                onChangeText={(text) => {
                    setInputText(text);
                    onChange(serializeAddressValue({ address: text, lat: null, lng: null }));
                }}
                disabled={readOnly}
                placeholder={question.hintText ?? "Enter address"}
                onFocus={onFocus}
                style={styles.input}
                dense
            />
        );
    }

    return (
        <View>
            <TextInput
                mode="outlined"
                value={inputText}
                onChangeText={handleTextChange}
                disabled={readOnly}
                placeholder={question.hintText ?? "Start typing an address..."}
                style={styles.input}
                dense
                onFocus={() => {
                    onFocus?.();
                    if (predictions.length > 0) {
                        setShowSuggestions(true);
                    }
                }}
                onBlur={() => {
                    // Delay hide so a touch on a suggestion fires first.
                    setTimeout(() => setShowSuggestions(false), 200);
                }}
            />
            {showSuggestions && (
                <View style={styles.suggestionsContainer}>
                    <FlatList
                        data={predictions}
                        keyExtractor={(item) => item.placeId}
                        keyboardShouldPersistTaps="handled"
                        renderItem={({ item }) => (
                            <TouchableOpacity
                                style={styles.suggestionItem}
                                onPress={() => handleSelect(item)}
                            >
                                <Text style={styles.suggestionText}>{item.description}</Text>
                            </TouchableOpacity>
                        )}
                    />
                </View>
            )}
            {parsed.lat !== null && parsed.lng !== null && (
                <Text style={styles.verified}>Address verified</Text>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    input: {
        backgroundColor: "#fff"
    },
    suggestionsContainer: {
        borderWidth: 1,
        borderColor: "#ddd",
        borderTopWidth: 0,
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        backgroundColor: "#fff",
        maxHeight: 200
    },
    suggestionItem: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: "#eee"
    },
    suggestionText: {
        fontSize: 14,
        color: "#333"
    },
    verified: {
        fontSize: 12,
        color: "#16a34a",
        marginTop: 4
    }
});
